'use client';

import { Fragment, lazy, ReactElement, Suspense } from 'react';

import LINKS from '@/constants/links';
import { shouldOpenNewTab } from '@/utils/helpers/routes';

import AnimatedNextLink from '../CustomNextLink';
import SocialIcons from '../SocialIcons';

const MobileFooterAccordion = lazy(() => import('./MobileFooterAccordion'));

const Footer = (): ReactElement => (
	<footer className="relative z-10 flex-[0_0_auto] bg-[#E2E7F5] p-0 dark:bg-[#0A121B]">
		<div className="max-w-[1500px] px-4 pb-8 pt-6 md:pb-10 md:pt-12">
			<div className="block md:hidden">
				<SocialIcons wrapperClassName="mt-4" iconClassName="text-gray dark:text-white" />
				<hr className="h-divider mx-auto mb-4 mt-6 border-gray md:mb-6" />
			</div>
			<div className="flex flex-col flex-wrap justify-between md:flex-row md:gap-7">
				{LINKS.FOOTER.DEFAULT.map(({ sectionTitle, links, sectionClass }) => (
					<Fragment key={sectionTitle}>
						<div className={`hidden flex-col md:flex ${sectionClass || ''}`}>
							<p className="font-medium uppercase text-midnight dark:text-white">{sectionTitle}</p>
							<div className={`mt-4 space-y-2.5`}>
								{links.map(({ title, url, openNewTab }) => (
									<Fragment key={title}>
										<AnimatedNextLink
											href={url}
											target={openNewTab || !shouldOpenNewTab(sectionTitle) ? '_blank' : '_self'}
											className="text-sm font-medium tracking-[0.0625rem] text-[#5E5D70] transition-all duration-200 ease-in-out hover:text-primary sm:text-sm"
											rel="noreferrer">
											{title}
										</AnimatedNextLink>
									</Fragment>
								))}
							</div>
						</div>
						<div className="block md:hidden">
							<Suspense>
								<MobileFooterAccordion key={sectionTitle} sectionTitle={sectionTitle} links={links} />
							</Suspense>
						</div>
					</Fragment>
				))}
				<div className="hidden w-52 md:block">
					<p className="font-semibold uppercase text-midnight dark:text-white">Social</p>
					<SocialIcons wrapperClassName="mt-4" iconClassName="text-[#5E5D70] dark:text-[#5E5D70] hover:!text-primary" />
				</div>
			</div>
		</div>

		{/* Copyright section */}
		<div className="bg-[#CCD0E0] dark:bg-[#080F17]">
			<div className="max-w-7xl p-4">
				<div className="flex h-full flex-col items-start gap-1">
					<div className="gap -1 flex flex-row flex-wrap">
						{LINKS.FOOTER.COPYRIGHT.map(({ title, url }, index) => (
							<div key={title} className="flex flex-row flex-wrap">
								<AnimatedNextLink
									key={title}
									href={url}
									target="_blank"
									rel="noreferrer"
									className="text-sm font-medium capitalize leading-[1.71] tracking-normal text-[#5E5D70] dark:text-[#5E5D70]">
									{title}
								</AnimatedNextLink>
								<span className="mx-2 flex items-center text-sm text-[#5E5D70] dark:text-[#5E5D70]">
									{index + 1 < LINKS.FOOTER.COPYRIGHT.length && ' • '}
								</span>
							</div>
						))}
					</div>
					<p className="text-sm font-medium capitalize tracking-normal text-[#5E5D70] dark:text-[#5E5D70]">
						&copy;{`${new Date().getFullYear()} Supra All Rights Reserved.`}
					</p>
				</div>
			</div>
		</div>
	</footer>
);

export default Footer;
